<template>
  <section>
    <filter-expression
        :columns="filterColumns"
        :dataset-name="datasetName"
        :filter-groups="filterGroups"
        :operator="operator"
        mode="simple"
        @filters-updated="handleFiltersUpdated"
        @operator-changed="handleOperatorChange" />
    <div class="table-list-wrapper">
      <div class="table-container">
        <div class="info-badge table-info-badge" :class="{ invisible: totalCount === -1 }">
          <span class="badge-text table-badge-text">{{ tableBadgeText }}</span>
          <!-- <span v-if="showSelectAllLink"><a class="badge-link" @click="handleSelectAllClicked">{{ selectAllLinkText }}</a></span> -->
          <span v-if="selectionLimitReached && dynamicFilter"><a class="badge-link" @click="handleDynamicFilterClicked">Add unlimited devices with Dynamic Filter</a></span>
        </div>
        <vyTable
          :columns="columns"
          :dataIndex="dataIndex"
          :pageSize="pageSize"
          :autoFetchConfig="autoFetchConfig"
          :setFirstPage="setFirstPage"
          :disableRowSelection="selectionLimitReached"
          :showRowSelectionColumn="true"
          :selectedRows="selectedRowsForUpdate"
          :selectable="-1"
          :nested-field-separator="nestedFieldSeparator"
          @row-selection-changed="handleRowSelectionChange"
          @full-page-selected="handleFullPageSelected" />
          <!-- selectable="-1" means "unlimited row selection" -->
      </div>
      <div class="list-container">
        <div class="info-badge list-info-badge">
          <span class="badge-text list-badge-text">{{ listBadgeText }}</span>
          <span v-if="selectedData.length > 0" class="list-badge-delete-icon" @click.stop="handleRemoveAllSelections"><i class="far fa-trash-alt"></i></span>
        </div>
        <div class="selections-container">
          <div class="selection-container" v-for="selection in selectedData" :key="selection[dataIndex]">
            <div class="selection">
              <span class="selection-text">{{selection[displayField]}}</span>
              <span class="selection-delete-icon" @click.stop="handleRemoveSelection(selection)"><i class="far fa-trash-alt"></i></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import VyTable from '../../table/src/table.vue';
import FilterExpression from '../../filterExpression/src/filterExpression.vue';
import VueDatasetService from "../../../services/vue-dataset-view.service";
import DashboardContainer from "../../dashboardContainer/src/dashboardContainer.vue";

export default {
  name: 'DataSelectionTable',
  props: {
    columns: { type: Array, required: true },
    filterColumns: { type: Array, required: true },
    datasetMetadata: { type: Object, required: true },
    // queryFunction should return a promise that resolves with an object that has a 'last_page' (int), a 'data' (array), and a 'total_count' (int) property.
    // signature: queryFunction(page, filterExpression, sorters)
    queryFunction: {type: Function, required: true },
    dataIndex: { type: String, required: true },
    displayField: { type: String, required: true },
    datasetName: { type: String, default: 'rows' }, // This should be a PLURAL noun to describe the data, e.g. "Endpoints", "Calls", etc.
    defaultSelections: Array,
    dynamicFilter: { type: Boolean, default: false},
    nestedFieldSeparator: { type: String },
    pageSize: { type: Number, default: 10 },
    maxSelections: { type: Number, default: 100 }
  },
  data: function() {
    let self = this;
    return {
      setFirstPage: 1,
      selectedData: [],
      selectedRowsForUpdate: [], // This allows us to update the table component's selected rows when the user removes selections from the list
      fullPageSelected: false,
      tableSorters: [],
      totalCount: -1,
      lastPage: 1,
      operator: 'and',
      filterGroups: [{
        operator: 'and',
        filters: []
      }],
      autoFetchConfig: {
        url: '',
        ajaxRequestFunc: function(url, config, tableParams) {
          return new Promise((resolve, reject) => {
            // copy to new object to avoid mutating original params
            let requestParams = Object.assign({}, tableParams);
            // convert from 1-index to 0-index pagination
            let page = requestParams.page ? requestParams.page - 1 : 0;
            let filterExpression = {
              operator: self.operator,
              filterGroups: self.filterGroups
            };
            self.tableSorters = requestParams.sorters;
            self.queryFunction(page, filterExpression, self.tableSorters).then(result => {
              self.totalCount = result.total_count || -1;
              self.lastPage = result.last_page || 1;
              resolve({
                last_page: self.lastPage || 1,
                data: result.data || []
              });
            }).catch(err => {
              reject(err);
            });
          });
        }
      }
    }
  },
  computed: {
    tableBadgeText: function() {
      let self = this;
      let rowsAvailable = self.totalCount - self.selectedData.length;
      let datasetName = self.datasetName;
      // This is a little hacky, but it's already up to the user of this component to input a plural noun for the datasetName,
      // so if this fails to "singularize" the string, the datasetName likely already messes up the copy.
      if (rowsAvailable === 1 && datasetName.endsWith('s')) {
        datasetName = datasetName.slice(0, -1);
      }

      if (self.selectionLimitReached) {
        return `${self.maxSelections} device limit reached.`;
      }

      if (self.fullPageSelected) {
        return `All ${self.pageSize} ${datasetName} on this page are selected.`;
      }

      return `Available ${datasetName}`;
    },
    listBadgeText: function() {
      let self = this;
      let datasetName = self.datasetName;
      let selectedCount = self.selectedData.length;
      if (selectedCount === 1 && datasetName.endsWith('s')) {
        datasetName = datasetName.slice(0, -1);
      }
      return `${selectedCount} ${datasetName} added`;
    },
    // selectAllLinkText: function() {
    //   return `Select all ${this.totalCount} ${this.datasetName} on all pages`;
    // },
    // showSelectAllLink: function() {
    //   return this.fullPageSelected && this.selectedData.length !== this.totalCount;
    // },
    selectionLimitReached: function() {
      return this.selectedData.length === this.maxSelections;
    }
  },
  watch: {
    selectedData: function(data) {
      /**
       * This event fires whenever the selected data changes
       */
      this.$emit('selections-updated', data);
    },
    defaultSelections: function(selections) {
      let self = this;
      self.selectedData = selections.sort(self.sortFunction);
      self.selectedRowsForUpdate = self.selectedData;
    }
  },
  components: {
    DashboardContainer,
    VyTable,
    FilterExpression
  },
  methods: {
    handleFiltersUpdated: function(filterGroups) {
      this.filterGroups = filterGroups;
      this.reloadData();
    },
    handleOperatorChange: function(operator) {
      this.operator = operator;
      this.reloadData()
    },
    handleRowSelectionChange: function(rowData) {
      let self = this;
      let newData = rowData;
      let isOverMax = newData.length > self.maxSelections;
      if (isOverMax) {
        newData = newData.slice(0, self.maxSelections)
      }
      self.selectedData = newData.sort(self.sortFunction);
      if (isOverMax) {
        // if we over-selected rows (e.g. when the user clicks the header checkbox to select all current page rows),
        // we need to tell the table to update once we slice off the extra selections
        self.selectedRowsForUpdate = self.selectedData;
      }
    },
    handleFullPageSelected: function(fullPageSelected) {
      this.fullPageSelected = fullPageSelected;
    },
    // handleSelectAllClicked() {
    //   let self = this;
    //   let promises = [];
    //   let allData = [];

    //   let filterExpression = {
    //     operator: self.operator,
    //     filterGroups: self.filterGroups
    //   };

    //   for (let i=0, _i=self.lastPage; i<_i; i++) {
    //     promises.push(self.queryFunction(i, filterExpression, self.tableSorters));
    //   }

    //   Promise.all(promises).then(results => {
    //     allData = results.flatMap(res => res.data || []);
    //     self.selectedData = allData.sort(self.sortFunction);
    //     self.selectedRowsForUpdate = self.selectedData;
    //   });
    // },
    handleDynamicFilterClicked: function() {
      // TODO: This needs to be updated to be generic. Currently this component is making assumptions about how and where it will be used.
      //       In future use cases there may not be a corresponding "dynamic filter" alongside this component.
      this.$emit('dynamic-filter-clicked');
    },
    handleRemoveSelection: function(selectionObject) {
      let self = this;
      self.selectedData = self.selectedData.filter(obj => obj[self.dataIndex] !== selectionObject[self.dataIndex]);
      self.selectedRowsForUpdate = self.selectedData;
    },
    handleRemoveAllSelections: function() {
      this.selectedData = [];
      this.selectedRowsForUpdate = [];
    },
    reloadData: function() {
      // this triggers a reload of the table data
      this.setFirstPage++;
    },
    sortFunction: function(a, b) {
      let self = this;
      let aName = a[self.displayField] ? a[self.displayField].toUpperCase() : '';
      let bName = b[self.displayField] ? b[self.displayField].toUpperCase() : '';
      if (aName < bName) {
        return -1;
      }
      if (aName > bName) {
        return 1;
      }
      return 0;
    }
  },
  created: function() {
    let self = this;
    self.filterGroups = VueDatasetService.getQuickStartFilterGroups(self.datasetName, self.datasetMetadata);
  },
  mounted: function() {
    let self = this;

    // initial data load
    self.reloadData();
    if (self.defaultSelections) {
      let selections = [...self.defaultSelections]; // avoid mutating the prop data
      self.selectedData = selections.sort(self.sortFunction);
      self.selectedRowsForUpdate = self.selectedData;
    }

    const fromEl = document.querySelector('.data-selection-table .vyopta-filter-expression');
    const toEl = document.querySelector('.data-selection-table');

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {

        const styles = window.getComputedStyle(entry.target);

        const marginTop = parseFloat(styles.marginTop) || 0;
        const marginBottom = parseFloat(styles.marginBottom) || 0;

        const newHeight = entry.contentRect.height + marginTop + marginBottom;

        toEl.style.setProperty('--filter-height', newHeight + 'px');
      }
    });

    observer.observe(fromEl);

  }
}
</script>
<style lang="scss" scoped>
section {
  padding: 20px;
  .table-list-wrapper {
    display: flex;
    margin-top: 20px;

    .info-badge {
      padding: 5px;
      border-radius: var(--border-radius);
      text-align: center;
      font-weight: 600;
    }

    .table-container {
      flex: 2;
      max-width: 66%;
      padding-right: 20px;

      .table-info-badge {
        margin-bottom: 4px;

        .badge-link {
          color: var(--hover-color);
          cursor: pointer;
        }
      }

      .vy-table-wrapper ::v-deep .vy-table.tabulator {
        height: 504px;
      }
    }

    .list-container {
      flex: 1;
      max-width: 33%;
      padding-left: 20px;

      .list-info-badge {
        position: relative;
        background-color: var(--active-background);
        margin-bottom: 26px;

        .list-badge-delete-icon {
          cursor: pointer;
          position: absolute;
          right: 16px;
        }
      }

      .selections-container {
        position: relative;
        border-radius: var(--border-radius);
        border: 1px solid #BFBFBF;
        height: 500px; // this should be the same height as the table
        overflow-y: auto;

        .selection-container {
          padding: 12px;
          &:nth-child(even) {
            background-color: var(--background-color);
          }
          .selection {
            max-width: 90%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            .selection-delete-icon {
              cursor: pointer;
              position: absolute;
              right: 16px;
            }
          }
        }
      }
    }
  }

  .invisible {
    visibility: hidden;
  }
}
</style>
<style lang="scss">

.table-list-wrapper .table-container .column-select .modal-opener {
  margin-bottom: 4px;
}
</style>